import type { ReactNode } from 'react';
import type { Messages } from './index';

/**
 * Portuguese (pt) translation dictionary. Typed as `Messages` so it must mirror
 * the English shape exactly — a missing or mistyped key fails the build.
 */
// Nomes de exibição dos códigos de pose do quiosque, mostrados no seletor
// aberto por toque longo em "Tirar uma fotografia".
const poseNames: Record<string, string> = {
  'por/uma-mao': 'Uma mão',
  'por/bracos-cruzados': 'Braços cruzados',
};

export const pt: Messages = {
  common: {
    loading: 'A carregar…',
    loadingSr: 'A carregar',
    tryAgain: 'Tentar novamente',
    close: 'Fechar',
    today: 'Hoje',
    yesterday: 'Ontem',
  },
  account: {
    label: 'Conta',
    signedInAs: 'Sessão iniciada como',
    signOut: 'Terminar sessão',
  },
  language: {
    label: 'Idioma',
    menuTitle: 'Mudar idioma',
    en: 'Inglês',
    pt: 'Português',
  },
  theme: {
    switchToLight: 'Mudar para o modo claro',
    switchToDark: 'Mudar para o modo escuro',
    lightTitle: 'Modo claro',
    darkTitle: 'Modo escuro',
  },
  day: {
    previous: 'Dia anterior',
    earliest: 'Dia mais antigo disponível',
    next: 'Dia seguinte',
    select: 'Selecionar dia',
    today: 'Hoje',
    live: 'Ao vivo',
    liveTitle: 'As novas fotografias aparecem automaticamente',
    photoCount: (n: number): string => `${n} ${n === 1 ? 'foto' : 'fotos'}`,
  },
  snapshot: {
    take: 'Tirar uma fotografia',
    sending: 'A acionar…',
    sent: 'Fotografia acionada',
    error: 'Não foi possível contactar o quiosque. Verifique a ligação e tente novamente.',
    denied: 'Permissão negada. As regras desta base de dados podem ainda não incluir o acionador do quiosque.',
    choose: 'Escolha a pose',
    chooseCancel: 'Cancelar',
    pose: (code: string): string => poseNames[code] ?? code,
  },
  qrTrigger: {
    label: 'QR no ecrã (disparo por telemóvel)',
    on: 'Ligado',
    off: 'Desligado',
    error: 'Não foi possível guardar',
  },
  sound: {
    label: 'Som do quiosque',
    on: 'Ligado',
    muted: 'Silenciado',
    volume: 'Volume',
    error: 'Não foi possível guardar',
  },
  kiosk: {
    settings: 'Definições do quiosque',
    ready: 'Quiosque pronto',
    offline: 'Quiosque offline',
    busy: 'Quiosque ocupado',
    gettingReady: 'A preparar…',
    capturing: 'A capturar…',
    finishing: 'A terminar…',
    errored: 'Erro no quiosque',
  },
  toolbar: {
    more: 'Mais ações',
  },
  photo: {
    unavailable: 'Imagem indisponível',
    alt: (time: string): string => `Fotografia tirada às ${time}`,
    printAria: (time: string): string => `Imprimir fotografia tirada às ${time}`,
    print: 'Imprimir',
    saveAria: (time: string): string => `Guardar fotografia tirada às ${time}`,
    save: 'Guardar',
    saveError: 'Erro ao guardar',
    saveRetry: 'Toque para guardar',
    deleteAria: (time: string): string => `Eliminar fotografia tirada às ${time}`,
    delete: 'Eliminar',
  },
  gallery: {
    dialogLabel: 'Visualizador de fotografias',
    openAria: (time: string): string =>
      `Ver a fotografia tirada às ${time} em ecrã inteiro`,
    counter: (n: number, total: number): string => `${n} de ${total}`,
    prev: 'Fotografia anterior',
    next: 'Fotografia seguinte',
  },
  selection: {
    enter: 'Selecionar',
    enterAria: 'Selecionar várias fotografias para guardar',
    cancel: 'Cancelar',
    selectedCount: (n: number): string =>
      n === 1 ? '1 selecionada' : `${n} selecionadas`,
    toggleAria: (time: string): string => `Selecionar fotografia tirada às ${time}`,
    save: (n: number): string =>
      n === 0 ? 'Guardar' : n === 1 ? 'Guardar 1 foto' : `Guardar ${n} fotos`,
    saving: 'A guardar…',
    retryHint: 'Pronto — toque em Guardar para concluir.',
    error: 'Erro ao guardar. Tente novamente.',
    partial: (n: number): string =>
      n === 1
        ? '1 fotografia não foi guardada.'
        : `${n} fotografias não foram guardadas.`,
  },
  print: {
    dialogLabel: 'Imprimir',
    confirmTitle: 'Imprimir esta fotografia?',
    confirmBody: (time: string): string => `Enviar a fotografia das ${time} para a impressora.`,
    confirm: 'Imprimir',
    cancel: 'Cancelar',
    sending: 'A enviar para a impressora…',
    queued: 'A aguardar a impressora…',
    printing: 'A imprimir…',
    printed: 'Enviado para a impressora ✓',
    printedTitle: 'Enviado para a impressora',
    printedBody: (time: string): string => `A sua fotografia das ${time} está a caminho da impressora.`,
    done: 'Concluído',
    backgroundHint: 'Pode fechar e continuar a trabalhar — a impressão continua.',
    failed: 'A impressora reportou um problema. Tente novamente.',
    error: 'Não foi possível contactar o serviço de impressão. Verifique a ligação e tente novamente.',
  },
  deleteDialog: {
    dialogLabel: 'Eliminar fotografia',
    title: 'Eliminar esta fotografia?',
    body: 'Isto remove permanentemente a captura para todos. Não é possível anular.',
    cancel: 'Cancelar',
    confirm: 'Eliminar',
    deleting: 'A eliminar…',
    error: 'Não foi possível eliminar a fotografia. Verifique a ligação e tente novamente.',
  },
  endOfFeed: {
    title: 'Não há mais fotografias',
    sub: (n: number): string =>
      `Chegou ao fim — ${n} ${n === 1 ? 'foto' : 'fotos'} neste dia.`,
  },
  states: {
    loadingPhotos: 'A carregar fotografias…',
    accessDeniedTitle: 'Acesso negado',
    accessDeniedMessage:
      'A sua conta não tem permissão para ver estas fotografias. Termine a sessão e inicie sessão com uma conta de administrador.',
    loadErrorTitle: 'Não foi possível carregar as fotografias',
    loadErrorMessage:
      'Ocorreu um problema ao contactar a base de dados. Verifique a ligação — a grelha será atualizada automaticamente.',
    noPhotos: 'Não há fotografias neste dia.',
    completingSignIn: 'A concluir o início de sessão…',
    checkingAccess: 'A verificar o acesso…',
  },
  signIn: {
    leadConfirm: 'Confirme o endereço de email para o qual este link de início de sessão foi enviado.',
    leadDefault:
      'Inicie sessão com o seu email — enviamos-lhe um link de início de sessão seguro. Sem necessidade de palavra-passe.',
    emailLabel: 'Endereço de email',
    working: 'A processar…',
    complete: 'Concluir início de sessão',
    send: 'Enviar link de início de sessão',
    startOver: 'Recomeçar',
    note: 'O acesso está limitado aos administradores do museu.',
  },
  notAuthorized: {
    heading: 'Não autorizado',
    leadWithEmail: (email: ReactNode): ReactNode => (
      <>
        A conta <strong>{email}</strong> não é administradora do museu.
      </>
    ),
    leadNoEmail: 'Esta conta não é administradora do museu.',
    note: 'O acesso ao backoffice requer uma função de administrador. Peça a um administrador para conceder acesso à sua conta e, em seguida, inicie sessão novamente.',
    useDifferent: 'Usar uma conta diferente',
  },
  config: {
    heading: 'Configuração necessária',
    missingIntro: 'A aplicação não pode arrancar porque estas variáveis de ambiente não estão definidas:',
    missingHelp: (example: ReactNode, local: ReactNode, apiKey: ReactNode, appId: ReactNode): ReactNode => (
      <>
        Copie {example} para {local}, preencha os valores (o {apiKey} e o {appId} encontram-se na
        consola do Firebase → Definições do projeto → a sua aplicação Web) e, em seguida, reinicie
        o servidor de desenvolvimento. Consulte o README para mais detalhes.
      </>
    ),
    initFailed: 'A aplicação não conseguiu inicializar o Firebase.',
    initHelp: (vitePrefix: ReactNode, local: ReactNode, dbUrl: ReactNode): ReactNode => (
      <>
        Verifique se os valores {vitePrefix} em {local} estão corretos (especialmente {dbUrl}) e,
        em seguida, reinicie o servidor de desenvolvimento.
      </>
    ),
  },
  auth: {
    yourAccount: 'a sua conta',
    linkSent: (email: string): string =>
      `Enviámos um link de início de sessão para ${email}. Abra-o neste dispositivo para continuar.`,
    errors: {
      invalidEmail: 'Isto não parece ser um endereço de email válido.',
      missingEmail: 'Introduza o seu endereço de email.',
      expiredLink: 'Este link de início de sessão expirou ou já foi utilizado. Solicite um novo.',
      unauthorizedDomain:
        'O domínio desta aplicação não está autorizado para início de sessão. Adicione-o em Firebase Auth → Domínios autorizados.',
      network: 'Erro de rede. Verifique a ligação e tente novamente.',
      tooManyRequests: 'Demasiadas tentativas. Aguarde um momento e tente novamente.',
      generic: 'Algo correu mal. Tente novamente.',
    },
  },
};
